/**
 * Vođene sesije (APP3): categories stacked vertically, each a horizontal row of cards with
 * its session count and "Prikaži sve"; search and the duration filter on top.
 * `?category=<group key>` shows one category in full; an unknown key is replaced by the overview.
 * `?duration=<sos|standard|deep>` turns the duration filter on when the page opens (Početna).
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { GuidedFilters, HorizontalCarousel, SessionTile } from '@/components/features';
import { useFavorites, useSessionGroups, useSessions } from '@/hooks';
import { useIsAuthenticated, useLanguage } from '@/store';
import { ROUTES } from '@/config/routes';
import {
  DURATION_FILTERS,
  groupSessions,
  matchesDuration,
  matchesSearch,
  UNGROUPED_GROUP_KEY,
  type DurationFilter,
} from '@/utils/sessionBrowse';
import {
  getSessionCountLabel,
  getSessionFiltersTranslation,
  getSessionsTranslation,
  type SessionsTranslationKey,
} from '@/utils/translations';
import type { Language, LocalizedSession, Session, SessionGroup } from '@/types';

type SessionData = Session | LocalizedSession;

function isDurationFilter(value: string | null): value is DurationFilter {
  return value !== null && (DURATION_FILTERS as readonly string[]).includes(value);
}

interface RowProps {
  group: SessionGroup;
  sessions: SessionData[];
  favoriteIds: ReadonlySet<string>;
  language: Language;
}

// The row's name lives on its carousel region; the <section> stays unnamed so there is one landmark per row.
function CategoryRow({ group, sessions, favoriteIds, language }: RowProps) {
  const headingId = useId();
  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 id={headingId} className="text-lg font-semibold sm:text-xl">
            {group.name}
          </h2>
          <p className="text-sm text-muted-foreground">{getSessionCountLabel(sessions.length, language)}</p>
        </div>
        <Link
          to={ROUTES.guidedCategory(group.key)}
          aria-describedby={headingId}
          className="-mr-2 inline-flex min-h-11 shrink-0 items-center gap-0.5 rounded-md px-2 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {getSessionsTranslation('showAll', language)}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
      <HorizontalCarousel label={group.name}>
        {sessions.map((session) => (
          <SessionTile key={session.id} session={session} isFavorite={favoriteIds.has(session.id)} />
        ))}
      </HorizontalCarousel>
    </section>
  );
}

interface CategoryViewProps extends RowProps {
  emptyMessage: string;
}

function CategoryView({ group, sessions, favoriteIds, language, emptyMessage }: CategoryViewProps) {
  return (
    <section className="space-y-3">
      <Link
        to={ROUTES.guided}
        className="-ml-2 inline-flex min-h-11 items-center gap-0.5 rounded-md px-2 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        {getSessionFiltersTranslation('categories', language)}
      </Link>
      <div>
        <h2 className="text-xl font-semibold sm:text-2xl">
          {group.name}
        </h2>
        <p className="text-sm text-muted-foreground">{getSessionCountLabel(sessions.length, language)}</p>
      </div>
      {sessions.length === 0 ? (
        <p className="py-12 text-center text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {sessions.map((session) => (
            <li key={session.id}>
              <SessionTile session={session} isFavorite={favoriteIds.has(session.id)} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function SessionsPage() {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const t = (key: SessionsTranslationKey) => getSessionsTranslation(key, language);

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryKey = searchParams.get('category');
  const [searchQuery, setSearchQuery] = useState('');
  // ?duration= sets the filter when it arrives (on load or by a link while the page is open);
  // choosing another filter removes it, so a reload or Back does not bring the old one back.
  const durationParam = searchParams.get('duration');
  const [duration, setDuration] = useState<DurationFilter>(() =>
    isDurationFilter(durationParam) ? durationParam : 'all',
  );
  const [appliedDurationParam, setAppliedDurationParam] = useState(durationParam);
  if (durationParam !== appliedDurationParam) {
    setAppliedDurationParam(durationParam);
    if (isDurationFilter(durationParam)) setDuration(durationParam);
  }
  const changeDuration = (value: DurationFilter) => {
    setDuration(value);
    if (searchParams.has('duration')) {
      const next = new URLSearchParams(searchParams);
      next.delete('duration');
      setSearchParams(next, { replace: true });
    }
  };

  const sessionsQuery = useSessions();
  const groupsQuery = useSessionGroups();
  const { data: favorites } = useFavorites();

  // "Prikaži sve" and the back link change only the query string: start the new view at the top.
  const shownCategory = useRef(categoryKey);
  useEffect(() => {
    if (shownCategory.current === categoryKey) return;
    shownCategory.current = categoryKey;
    window.scrollTo(0, 0);
  }, [categoryKey]);

  const favoriteIds = useMemo<ReadonlySet<string>>(
    () => new Set(isAuthenticated && favorites ? favorites.map((s: SessionData) => s.id) : []),
    [favorites, isAuthenticated],
  );

  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  const otherSessions = getSessionsTranslation('otherSessions', language);
  const fallbackGroup = useMemo<SessionGroup>(
    () => ({ key: UNGROUPED_GROUP_KEY, name: otherSessions, position: Number.MAX_SAFE_INTEGER }),
    [otherSessions],
  );
  // Every published session, filters aside: decides which rows (and ?category= keys) exist.
  const allRows = useMemo(
    () => groupSessions<SessionData>(sessionsQuery.data ?? [], groups, fallbackGroup),
    [sessionsQuery.data, groups, fallbackGroup],
  );
  const rows = useMemo(() => {
    const names = new Map(groups.map((group) => [group.key, group.name]));
    const visible = (sessionsQuery.data ?? []).filter(
      (session: SessionData) =>
        matchesDuration(session.duration, duration) &&
        matchesSearch(session, names.get(session.group_key ?? '') ?? fallbackGroup.name, searchQuery, language),
    );
    return groupSessions(visible, groups, fallbackGroup);
  }, [sessionsQuery.data, groups, fallbackGroup, duration, searchQuery, language]);

  const categories = useMemo(
    () => [...groups, ...(allRows.some((row) => row.group.key === UNGROUPED_GROUP_KEY) ? [fallbackGroup] : [])],
    [groups, allRows, fallbackGroup],
  );
  const selectedGroup = categoryKey ? categories.find((group) => group.key === categoryKey) : undefined;

  // A stale or mistyped ?category= would otherwise show the overview under the wrong address.
  const navigate = useNavigate();
  const loaded = !sessionsQuery.isLoading && !groupsQuery.isLoading && !sessionsQuery.error && !groupsQuery.error;
  const unknownCategory = loaded && categoryKey !== null && !selectedGroup;
  useEffect(() => {
    if (unknownCategory) navigate(ROUTES.guided, { replace: true });
  }, [unknownCategory, navigate]);
  const filtering = searchQuery.trim() !== '' || duration !== 'all';
  const emptyMessage = filtering ? t('noSearchResults') : t('noCategoryResults');

  let content;
  if (sessionsQuery.isLoading || groupsQuery.isLoading) {
    content = (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  } else if (sessionsQuery.error || groupsQuery.error) {
    content = <p className="py-16 text-center text-destructive">{t('loadError')}</p>;
  } else if (selectedGroup) {
    content = (
      <CategoryView
        group={selectedGroup}
        sessions={rows.find((row) => row.group.key === selectedGroup.key)?.sessions ?? []}
        favoriteIds={favoriteIds}
        language={language}
        emptyMessage={emptyMessage}
      />
    );
  } else if (rows.length === 0) {
    content = <p className="py-16 text-center text-muted-foreground">{emptyMessage}</p>;
  } else {
    content = (
      <div className="space-y-8">
        {rows.map((row) => (
          <CategoryRow
            key={row.group.key}
            group={row.group}
            sessions={row.sessions}
            favoriteIds={favoriteIds}
            language={language}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="container py-4 sm:py-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="mb-2 text-2xl font-bold sm:text-3xl">{t('pageTitle')}</h1>
        <p className="text-sm text-muted-foreground sm:text-base">{t('pageSubtitle')}</p>
      </div>

      <GuidedFilters
        searchQuery={searchQuery}
        duration={duration}
        onSearchChange={setSearchQuery}
        onDurationChange={changeDuration}
      />

      <div className="mt-6">{content}</div>
    </div>
  );
}
